import React, { useContext, useState, useMemo, useEffect, useRef } from "react";
import { RadioButton } from "primereact/radiobutton";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { AddPropertyContext } from "./AddPropertyContext";
import { Property } from "../../../interface/tracker";
import {
  convertToMMDDYYYY,
  getDateStringWithGivenFormat,
  getCurrentFinancialYear,
  isMMDDYYFormat,
} from "../../../utils/commonUtil";
import { DATE_FORMAT } from "../../../utils/constants";

interface RentAndExpensesProps {
  onNext: () => void;
  onBack: () => void;
  isEditMode?: boolean;
}

const STR = {
  FIELD_PRO_RATA_EXPENSE: "Year1ProRataDateExpense",
  ERR_PRO_RATA_EXPENSE_REQUIRED: "Pro rata expenses from date is required.",
  ERR_PRO_RATA_INCOME_REQUIRED: "Pro rata rent income from date is required.",
  ERR_RENTAL_WEEKS: "Rate of occupancy must be between 1 and 52 weeks.",
  ERR_LEASE_END_DATE: "Lease end date must be after lease start date.",
  ERR_NEXT_RENT_REVIEW_DATE: "Next rent review date must be after lease start date.",
  ERR_WEEKLY_RENT: "Weekly rent must be between greater than 0.",
  ERR_LEASE_START_DATE: (min: string, max: string) => `Lease start date must be between ${min} and ${max}.`,
  FIELD_LEASE_TENANTS: "Lease_Tenants",
  FIELD_LEASE_REVIEW_DATE: "Lease_ReviewDate",
  FIELD_LEASE_END_DATE: "Lease_EndDate",
  FIELD_LEASE_START_DATE: "Lease_StartDate",
  FIELD_PRO_RATA_INCOME: "Year1ProRataDateIncome",
  LOADING: "Loading...",
};

const RentAndExpenses = ({ onNext, onBack, isEditMode = false }: RentAndExpensesProps) => {
  const context = useContext(AddPropertyContext);
  const [leaseStartDate, setLeaseStartDate] = useState<Date | null>(null);
  const [leaseEndDate, setLeaseEndDate] = useState<Date | null>(null);
  const [rentReviewDate, setRentReviewDate] = useState<Date | null>(null);
  const [proRataExpenseDate, setProRataExpenseDate] = useState<Date | null>(null);
  const [proRataIncomeDate, setProRataIncomeDate] = useState<Date | null>(null);
  const [errors, setErrors] = useState({
    proRataExpenseDate: "",
    proRataIncomeDate: "",
    rentalWeeks: "",
    rentalWeeklyRent: "",
    leaseEndDate: "",
    nextRentReviewDate: "",
    leaseStartDate: "",
  });
  const isProRataExpenseDateChanged = useRef<boolean>(false);

  if (!context) {
    return <div>{STR.LOADING}</div>;
  }

  const { property, setProperty } = context;

  useEffect(() => {
    if (!isEditMode && (context.property.PurchasePrice || context.property.Current_InitialPurchasePrice)) {
      let purchaseAmount = 0;
      if (context.property && context.property.PurchaseType === "existing") {
        purchaseAmount = context.property.Current_InitialPurchasePrice || 0;
      } else {
        purchaseAmount = parseFloat((context.property.PurchasePrice ?? "").toString()) || 0;
      }
      context.setProperty({
        ...context.property,
        Expense_Rates:
          context.userProfile?.PropertyExpenseStandard.isRatePercentage == "1"
            ? purchaseAmount * ((context.userProfile?.PropertyExpenseStandard.RatesPercentage ?? 0) / 100)
            : context.userProfile?.PropertyExpenseStandard.Rates,
        Expense_Insurance:
          context.userProfile?.PropertyExpenseStandard.isInsurancePercentage == "1"
            ? purchaseAmount * ((context.userProfile?.PropertyExpenseStandard.InsurancePercentage ?? 0) / 100)
            : context.userProfile?.PropertyExpenseStandard.Insurance,
        Expense_RepairsMaintenance:
          context.userProfile?.PropertyExpenseStandard.isRepairPercentage == "1"
            ? purchaseAmount * ((context.userProfile?.PropertyExpenseStandard.RepairPercentage ?? 0) / 100)
            : context.userProfile?.PropertyExpenseStandard.Repair,
        Expense_BodyCorporateFees:
          context.userProfile?.PropertyExpenseSpecific.isBodyCorporatePercentage == "1"
            ? purchaseAmount * ((context.userProfile?.PropertyExpenseSpecific.BodyCorpatePercentage ?? 0) / 100)
            : context.userProfile?.PropertyExpenseSpecific.BodyCorporate,
      });
    }
  }, [context.property.PurchasePrice]);

  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    const parts = dateString.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JavaScript
      const year = parseInt(parts[2], 10);

      return new Date(year, month, day);
    }

    return null; // Return null if the format is invalid
  };

  useEffect(() => {
    if (context.property) {
      setLeaseStartDate(context.property.Lease_StartDate ? parseDate(context.property.Lease_StartDate) : null);
      setLeaseEndDate(context.property.Lease_EndDate ? parseDate(context.property.Lease_EndDate) : null);
      setRentReviewDate(context.property.Lease_ReviewDate ? parseDate(context.property.Lease_ReviewDate) : null);
      setProRataExpenseDate(
        context.property.Year1ProRataDateExpense ? parseDate(context.property.Year1ProRataDateExpense) : null
      );
      setProRataIncomeDate(
        context.property.Year1ProRataDateIncome ? parseDate(context.property.Year1ProRataDateIncome) : null
      );
    }
  }, [context.property]);

  const dateRangeMin = useMemo<Date | null>(() => {
    return property.PurchaseDate ? parseDate(property.PurchaseDate) : null;
  }, [property.PurchaseDate]);

  const dateRangeMax = useMemo<Date>(() => {
    const now = new Date();
    const fyEndYear = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
    return new Date(fyEndYear, 5, 30);
  }, []);

  const subtotalPeak = useMemo(() => {
    const weeks = Number(property.Rental_Peak_NumWeeks) || 0;
    const rent = Number(property.Rental_Peak_WeeklyRent) || 0;
    const occupancy = (Number(property.Rental_Peak_OccupancyRate) || 0) / 100;
    return parseInt((weeks * rent * occupancy).toFixed(0), 10);
  }, [property.Rental_Peak_NumWeeks, property.Rental_Peak_WeeklyRent, property.Rental_Peak_OccupancyRate]);

  const subtotalShoulder = useMemo(() => {
    const weeks = Number(property.Rental_Shoulder_NumWeeks) || 0;
    const rent = Number(property.Rental_Shoulder_WeeklyRent) || 0;
    const occupancy = (Number(property.Rental_Shoulder_OccupancyRate) || 0) / 100;
    return parseInt((weeks * rent * occupancy).toFixed(0), 10);
  }, [property.Rental_Shoulder_NumWeeks, property.Rental_Shoulder_WeeklyRent, property.Rental_Shoulder_OccupancyRate]);

  const subtotalOffPeak = useMemo(() => {
    const weeks = Number(property.Rental_OffPeak_NumWeeks) || 0;
    const rent = Number(property.Rental_OffPeak_WeeklyRent) || 0;
    const occupancy = (Number(property.Rental_OffPeak_OccupancyRate) || 0) / 100;
    return parseInt((weeks * rent * occupancy).toFixed(0), 10);
  }, [property.Rental_OffPeak_NumWeeks, property.Rental_OffPeak_WeeklyRent, property.Rental_OffPeak_OccupancyRate]);

  const handleInputChange = (field: keyof Property | string, value: any) => {
    if (setProperty) {
      setProperty((prev: Partial<Property>) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  useEffect(() => {
    if (property.Lease_StartDate) {
      const startDate = getFormattedDate(property.Lease_StartDate);
      if (startDate) {
        setLeaseStartDate(startDate);
      }
    } else {
      setLeaseStartDate(null);
    }
    if (property.Lease_EndDate) {
      const endDate = getFormattedDate(property.Lease_EndDate);
      if (endDate) {
        setLeaseEndDate(endDate);
      }
    } else {
      setLeaseEndDate(null);
    }
    if (property.Lease_ReviewDate) {
      const reviewDate = getFormattedDate(property.Lease_ReviewDate);
      if (reviewDate) {
        setRentReviewDate(reviewDate);
      }
    } else {
      setRentReviewDate(null);
    }
  }, [property.Lease_StartDate, property.Lease_EndDate, property.Lease_ReviewDate]);

  const getFormattedDate = (date: string) => {
    const d = new Date(date);

    const parts = date.split("/");
    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      d.setFullYear(year, month - 1, day);
    }
    return !isNaN(d.getTime()) ? d : null;
  };

  const handleDateChange = (
    field: keyof Property | string,
    date: Date | null,
    setter: React.Dispatch<React.SetStateAction<Date | null>>
  ) => {
    setter(date);

    if (setProperty) {
      setProperty((prev: Partial<Property>) => ({
        ...prev,
        [field]: date ? getDateStringWithGivenFormat(date, DATE_FORMAT.DDMMYYYY) : "",
      }));
    }
    if (field === STR.FIELD_LEASE_START_DATE) {
      if (setProperty && !isEditMode) {
        setProperty((prev: Partial<Property>) => ({
          ...prev,
          Year1ProRataDateIncome: date ? getDateStringWithGivenFormat(date, DATE_FORMAT.DDMMYYYY) : "",
        }));
      }
      // Validate lease start date range
      const purchaseMin = property.PurchaseDate ? parseDate(property.PurchaseDate) : null;
      const fyEndYear = 2000 + parseInt(getCurrentFinancialYear(), 10);
      const leaseMax = new Date(fyEndYear, 5, 30); // June 30 of current FY
      if (date && purchaseMin && (date < purchaseMin || date > leaseMax)) {
        setErrors((prev) => ({
          ...prev,
          leaseStartDate: STR.ERR_LEASE_START_DATE(
            getDateStringWithGivenFormat(purchaseMin, DATE_FORMAT.DDMMYYYY),
            getDateStringWithGivenFormat(leaseMax, DATE_FORMAT.DDMMYYYY)
          ),
        }));
      } else {
        setErrors((prev) => ({ ...prev, leaseStartDate: "" }));
      }
      if (date) {
        setErrors((prev) => ({
          ...prev,
          leaseEndDate: leaseEndDate && leaseEndDate <= date ? STR.ERR_LEASE_END_DATE : "",
          nextRentReviewDate: rentReviewDate && rentReviewDate <= date ? STR.ERR_NEXT_RENT_REVIEW_DATE : "",
        }));
      } else {
        setErrors((prev) => ({ ...prev, leaseEndDate: "", nextRentReviewDate: "" }));
      }
    }
    if (field === STR.FIELD_LEASE_END_DATE) {
      if (date && leaseStartDate && date <= leaseStartDate) {
        setErrors((prev) => ({ ...prev, leaseEndDate: STR.ERR_LEASE_END_DATE }));
      } else {
        setErrors((prev) => ({ ...prev, leaseEndDate: "" }));
      }
    }
    if (field === STR.FIELD_LEASE_REVIEW_DATE) {
      if (date && leaseStartDate && date <= leaseStartDate) {
        setErrors((prev) => ({ ...prev, nextRentReviewDate: STR.ERR_NEXT_RENT_REVIEW_DATE }));
      } else {
        setErrors((prev) => ({ ...prev, nextRentReviewDate: "" }));
      }
    }
    if (field === STR.FIELD_PRO_RATA_EXPENSE) {
      isProRataExpenseDateChanged.current = true;
      setProperty((prev: Partial<Property>) => ({
        ...prev,
        isProRatedExpenseDateManuallySet: true,
      }));
    }
  };

  const totalAnnualIncome = useMemo(() => {
    if (property.Rental_IsResidential === 1) {
      return (Number(property.Rental_WeeklyRent) || 0) * (Number(property.Rental_NumWeeks) || 0);
    }
    return subtotalPeak + subtotalShoulder + subtotalOffPeak;
  }, [
    property.Rental_IsResidential,
    property.Rental_WeeklyRent,
    property.Rental_NumWeeks,
    subtotalPeak,
    subtotalShoulder,
    subtotalOffPeak,
  ]);

  const totalAnnualExpenses = useMemo(() => {
    const expenses = [
      property.Expense_Rates,
      property.Expense_Insurance,
      property.Expense_LandLease,
      property.Expense_RepairsMaintenance,
      property.Expense_ServiceContracts,
      property.Expense_GardeningFees,
      property.Expense_LettingFees,
      property.Expense_BodyCorporateFees,
      property.Expense_CleaningFees,
      property.Expense_OtherExpenses,
    ];

    const management = totalAnnualIncome * ((property.Expense_AgentsRentalCommissionRate || 0) / 100);

    return expenses.reduce<number>((acc, current) => acc + (Number(current) || 0), 0) + management;
  }, [property]);

  const validate = () => {
    const newErrors = {
      proRataExpenseDate: "",
      proRataIncomeDate: "",
      rentalWeeks: "",
      rentalWeeklyRent: "",
      leaseStartDate: "",
      leaseEndDate: "",
      nextRentReviewDate: "",
    };
    let isValid = true;

    const dateRangeError = `Date must be between ${dateRangeMin ? getDateStringWithGivenFormat(dateRangeMin, DATE_FORMAT.DDMMYYYY) : "Initial Purchase Date"} and ${getDateStringWithGivenFormat(dateRangeMax, DATE_FORMAT.DDMMYYYY)}.`;
    const isInDateRange = (date: Date | null): boolean => {
      if (!date) return true;
      if (dateRangeMin && date < dateRangeMin) return false;
      if (date > dateRangeMax) return false;
      return true;
    };

    const purchaseMin = property.PurchaseDate ? parseDate(property.PurchaseDate) : null;
    const fyEndYear = 2000 + parseInt(getCurrentFinancialYear(), 10);
    const leaseMax = new Date(fyEndYear, 5, 30);
    if (leaseStartDate && purchaseMin && (leaseStartDate < purchaseMin || leaseStartDate > leaseMax)) {
      newErrors.leaseStartDate = STR.ERR_LEASE_START_DATE(
        getDateStringWithGivenFormat(purchaseMin, DATE_FORMAT.DDMMYYYY),
        getDateStringWithGivenFormat(leaseMax, DATE_FORMAT.DDMMYYYY)
      );
      isValid = false;
    }
    if (property.Year1ProRata === 1) {
      if (!property.Year1ProRataDateExpense) {
        newErrors.proRataExpenseDate = STR.ERR_PRO_RATA_EXPENSE_REQUIRED;
        isValid = false;
      } else if (!isInDateRange(proRataExpenseDate)) {
        newErrors.proRataExpenseDate = dateRangeError;
        isValid = false;
      }
      if (property.Lease_StartDate && !property.Year1ProRataDateIncome) {
        newErrors.proRataIncomeDate = STR.ERR_PRO_RATA_INCOME_REQUIRED;
        isValid = false;
      } else if (proRataIncomeDate && !isInDateRange(proRataIncomeDate)) {
        newErrors.proRataIncomeDate = dateRangeError;
        isValid = false;
      }
    }
    if (!property.Rental_NumWeeks || property.Rental_NumWeeks < 1 || property.Rental_NumWeeks > 52) {
      newErrors.rentalWeeks = STR.ERR_RENTAL_WEEKS;
      isValid = false;
    }
    if (!property.Rental_WeeklyRent || property.Rental_WeeklyRent < 1) {
      newErrors.rentalWeeklyRent = STR.ERR_WEEKLY_RENT;
      isValid = false;
    }
    if (property.Lease_StartDate && property.Lease_EndDate) {
      const startDate = parseDate(property.Lease_StartDate);
      const endDate = parseDate(property.Lease_EndDate);
      if (startDate && endDate && endDate <= startDate) {
        newErrors.leaseEndDate = STR.ERR_LEASE_END_DATE;
        isValid = false;
      }
    }
    if (property.Lease_ReviewDate && property.Lease_StartDate) {
      const startDate = parseDate(property.Lease_StartDate);
      const reviewDate = parseDate(property.Lease_ReviewDate);
      if (startDate && reviewDate && reviewDate <= startDate) {
        newErrors.nextRentReviewDate = STR.ERR_NEXT_RENT_REVIEW_DATE;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNextClick = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="p-fluid">
      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Annual rental returns</h3>
        </div>
        <div className="flex items-center">
          <div className="w-1/5">
            <label>Rental type:</label>
          </div>
          <div className="w-4/5 flex gap-4">
            <div className="flex items-center">
              <RadioButton
                inputId="residential"
                data-testid="apre-rdo-residential"
                name="rentalType"
                value={1}
                onChange={(e) => handleInputChange("Rental_IsResidential", e.value)}
                checked={property.Rental_IsResidential === 1}
              />
              <label htmlFor="residential" className="ml-2">
                Residential long term
              </label>
            </div>
            <div className="flex items-center">
              <RadioButton
                inputId="holiday"
                data-testid="apre-rdo-holiday"
                name="rentalType"
                value={0}
                onChange={(e) => handleInputChange("Rental_IsResidential", e.value)}
                checked={property.Rental_IsResidential === 0}
              />
              <label htmlFor="holiday" className="ml-2">
                Holiday rental
              </label>
            </div>
          </div>
        </div>
        {property.Rental_IsResidential === 1 ? (
          <div className="flex flex-row gap-8 mt-4">
            <div className="w-1/2 flex items-center">
              <label htmlFor="occupancy" className="w-1/3">
                Rate of occupancy (wks)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="occupancy"
                  data-testid="apre-sldr-occupancy"
                  value={property.Rental_NumWeeks}
                  onChange={(e: any) => handleInputChange("Rental_NumWeeks", e.value)}
                  className={`flex-grow ${errors.rentalWeeks ? "p-invalid" : ""}`}
                  min={1}
                  max={52}
                />
                <InputNumber
                  id="apre-num-occupancy"
                  data-testid="apre-num-occupancy"
                  value={property.Rental_NumWeeks}
                  onValueChange={(e) => handleInputChange("Rental_NumWeeks", e.value!)}
                  className={`w-1/3 ${errors.rentalWeeks ? "p-invalid" : ""}`}
                  min={1}
                  max={52}
                />
                {errors.rentalWeeks && <small className="p-error">{errors.rentalWeeks}</small>}
              </div>
            </div>
            <div className="w-1/2 flex items-center">
              <label htmlFor="weeklyRent" className="w-1/3">
                Weekly rent ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="weeklyRent"
                  data-testid="apre-sldr-weeklyrent"
                  value={(property.Rental_WeeklyRent || 0) >= 2000 ? 2000 : property.Rental_WeeklyRent}
                  onChange={(e: any) => handleInputChange("Rental_WeeklyRent", e.value)}
                  className={`flex-grow ${errors.rentalWeeklyRent ? "p-invalid" : ""}`}
                  min={0}
                  max={2000}
                />
                <InputNumber
                  id="apre-num-weeklyrent"
                  data-testid="apre-num-weeklyrent"
                  value={property.Rental_WeeklyRent}
                  onValueChange={(e) => handleInputChange("Rental_WeeklyRent", e.value!)}
                  className={"w-1/3 " + (errors.rentalWeeklyRent ? "p-invalid" : "")}
                />
                {errors.rentalWeeklyRent && <small className="p-error">{errors.rentalWeeklyRent}</small>}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-4">
            <div className="flex flex-row gap-4">
              <div className="w-1/4"></div>
              <div className="w-1/4 text-center">
                <b>Peak:</b>
              </div>
              <div className="w-1/4 text-center">
                <b>Shoulder:</b>
              </div>
              <div className="w-1/4 text-center">
                <b>Off peak:</b>
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center">
              <div className="w-1/4 "># of weeks</div>
              <div className="w-1/4  flex items-center gap-2">
                <Slider
                  id="apre-sldr-peakweeks"
                  data-testid="apre-sldr-peakweeks"
                  value={property.Rental_Peak_NumWeeks}
                  onChange={(e: any) => handleInputChange("Rental_Peak_NumWeeks", e.value)}
                  className="flex-grow"
                  min={0}
                  max={52}
                />
                <InputNumber
                  id="apre-num-peakweeks"
                  data-testid="apre-num-peakweeks"
                  value={property.Rental_Peak_NumWeeks}
                  onValueChange={(e) => handleInputChange("Rental_Peak_NumWeeks", e.value!)}
                  className="w-1/3"
                  max={52}
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-shoulderweeks"
                  data-testid="apre-sldr-shoulderweeks"
                  value={property.Rental_Shoulder_NumWeeks}
                  onChange={(e: any) => handleInputChange("Rental_Shoulder_NumWeeks", e.value)}
                  className="flex-grow"
                  min={0}
                  max={52}
                />
                <InputNumber
                  id="apre-num-shoulderweeks"
                  data-testid="apre-num-shoulderweeks"
                  value={property.Rental_Shoulder_NumWeeks}
                  onValueChange={(e) => handleInputChange("Rental_Shoulder_NumWeeks", e.value!)}
                  className="w-1/3"
                  max={52}
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-offpeakweeks"
                  data-testid="apre-sldr-offpeakweeks"
                  value={property.Rental_OffPeak_NumWeeks}
                  onChange={(e: any) => handleInputChange("Rental_OffPeak_NumWeeks", e.value)}
                  className="flex-grow"
                  min={0}
                  max={52}
                />
                <InputNumber
                  id="apre-num-offpeakweeks"
                  data-testid="apre-num-offpeakweeks"
                  value={property.Rental_OffPeak_NumWeeks}
                  onValueChange={(e) => handleInputChange("Rental_OffPeak_NumWeeks", e.value!)}
                  className="w-1/3"
                  max={52}
                />
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center">
              <div className="w-1/4">Occupancy (%)</div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-peakoccupancy"
                  data-testid="apre-sldr-peakoccupancy"
                  value={property.Rental_Peak_OccupancyRate}
                  onChange={(e: any) => handleInputChange("Rental_Peak_OccupancyRate", e.value)}
                  className="flex-grow"
                  min={0}
                  max={100}
                />
                <InputNumber
                  id="apre-num-peakoccupancy"
                  data-testid="apre-num-peakoccupancy"
                  value={property.Rental_Peak_OccupancyRate}
                  onValueChange={(e) => handleInputChange("Rental_Peak_OccupancyRate", e.value!)}
                  className="w-1/3"
                  max={100}
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-shoulderoccupancy"
                  data-testid="apre-sldr-shoulderoccupancy"
                  value={property.Rental_Shoulder_OccupancyRate}
                  onChange={(e: any) => handleInputChange("Rental_Shoulder_OccupancyRate", e.value)}
                  className="flex-grow"
                  min={0}
                  max={100}
                />
                <InputNumber
                  id="apre-num-shoulderoccupancy"
                  data-testid="apre-num-shoulderoccupancy"
                  value={property.Rental_Shoulder_OccupancyRate}
                  onValueChange={(e) => handleInputChange("Rental_Shoulder_OccupancyRate", e.value!)}
                  className="w-1/3"
                  max={100}
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-offpeakoccupancy"
                  data-testid="apre-sldr-offpeakoccupancy"
                  value={property.Rental_OffPeak_OccupancyRate}
                  onChange={(e: any) => handleInputChange("Rental_OffPeak_OccupancyRate", e.value)}
                  className="flex-grow"
                  min={0}
                  max={100}
                />
                <InputNumber
                  id="apre-num-offpeakoccupancy"
                  data-testid="apre-num-offpeakoccupancy"
                  value={property.Rental_OffPeak_OccupancyRate}
                  onValueChange={(e) => handleInputChange("Rental_OffPeak_OccupancyRate", e.value!)}
                  className="w-1/3"
                  max={100}
                />
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center">
              <div className="w-1/4">Weekly rent ($)</div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-peakrent"
                  data-testid="apre-sldr-peakrent"
                  value={(property.Rental_Peak_WeeklyRent || 0) >= 5000 ? 5000 : property.Rental_Peak_WeeklyRent}
                  onChange={(e: any) => handleInputChange("Rental_Peak_WeeklyRent", e.value)}
                  className="flex-grow"
                  min={0}
                  max={5000}
                />
                <InputNumber
                  id="apre-num-peakrent"
                  data-testid="apre-num-peakrent"
                  value={property.Rental_Peak_WeeklyRent}
                  onValueChange={(e) => handleInputChange("Rental_Peak_WeeklyRent", e.value!)}
                  className="w-1/3"
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-shoulderrent"
                  data-testid="apre-sldr-shoulderrent"
                  value={
                    (property.Rental_Shoulder_WeeklyRent || 0) >= 5000 ? 5000 : property.Rental_Shoulder_WeeklyRent
                  }
                  onChange={(e: any) => handleInputChange("Rental_Shoulder_WeeklyRent", e.value)}
                  className="flex-grow"
                  min={0}
                  max={5000}
                />
                <InputNumber
                  id="apre-num-shoulderrent"
                  data-testid="apre-num-shoulderrent"
                  value={property.Rental_Shoulder_WeeklyRent}
                  onValueChange={(e) => handleInputChange("Rental_Shoulder_WeeklyRent", e.value!)}
                  className="w-1/3"
                />
              </div>
              <div className="w-1/4 flex items-center gap-2">
                <Slider
                  id="apre-sldr-offpeakrent"
                  data-testid="apre-sldr-offpeakrent"
                  value={(property.Rental_OffPeak_WeeklyRent || 0) >= 5000 ? 5000 : property.Rental_OffPeak_WeeklyRent}
                  onChange={(e: any) => handleInputChange("Rental_OffPeak_WeeklyRent", e.value)}
                  className="flex-grow"
                  min={0}
                  max={5000}
                />
                <InputNumber
                  id="apre-num-offpeakrent"
                  data-testid="apre-num-offpeakrent"
                  value={property.Rental_OffPeak_WeeklyRent}
                  onValueChange={(e) => handleInputChange("Rental_OffPeak_WeeklyRent", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center">
              <div className="w-1/4">
                <b>subtotal</b>
              </div>
              <div className="w-1/4 text-end">
                <b>${subtotalPeak?.toLocaleString()}</b>
              </div>
              <div className="w-1/4 text-end">
                <b>${subtotalShoulder?.toLocaleString()}</b>
              </div>
              <div className="w-1/4 text-end">
                <b>${subtotalOffPeak?.toLocaleString()}</b>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Annual property expenses</h3>
        </div>
        <div className="flex flex-row gap-8">
          {/* Left Column */}
          <div className="w-1/2 flex flex-col gap-4">
            <div className="flex items-center">
              <label htmlFor="rates" className="w-1/3">
                Rates ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="rates"
                  data-testid="apre-sldr-rates"
                  value={(property.Expense_Rates || 0) >= 5000 ? 5000 : property.Expense_Rates}
                  onChange={(e: any) => handleInputChange("Expense_Rates", e.value)}
                  className="flex-grow"
                  max={5000}
                />
                <InputNumber
                  id="apre-num-rates"
                  data-testid="apre-num-rates"
                  value={property.Expense_Rates}
                  onValueChange={(e) => handleInputChange("Expense_Rates", e.value!)}
                  className="w-1/3"
                  maxFractionDigits={0}
                  minFractionDigits={0}
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="landLease" className="w-1/3">
                Land lease ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="landLease"
                  data-testid="apre-sldr-landlease"
                  value={(property.Expense_LandLease || 0) >= 50000 ? 50000 : property.Expense_LandLease}
                  onChange={(e: any) => handleInputChange("Expense_LandLease", e.value)}
                  className="flex-grow"
                  max={50000}
                />
                <InputNumber
                  id="apre-num-landlease"
                  data-testid="apre-num-landlease"
                  value={property.Expense_LandLease}
                  onValueChange={(e) => handleInputChange("Expense_LandLease", e.value!)}
                  className="w-1/3"
                  maxFractionDigits={0}
                  minFractionDigits={0}
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="serviceContracts" className="w-1/3">
                Service contracts ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="serviceContracts"
                  data-testid="apre-sldr-servicecontracts"
                  value={(property.Expense_ServiceContracts || 0) >= 1000 ? 1000 : property.Expense_ServiceContracts}
                  onChange={(e: any) => handleInputChange("Expense_ServiceContracts", e.value)}
                  className="flex-grow"
                  max={1000}
                />
                <InputNumber
                  id="apre-num-servicecontracts"
                  data-testid="apre-num-servicecontracts"
                  value={property.Expense_ServiceContracts}
                  onValueChange={(e) => handleInputChange("Expense_ServiceContracts", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="lettingFees" className="w-1/3">
                Letting fees ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="lettingFees"
                  data-testid="apre-sldr-lettingfees"
                  value={(property.Expense_LettingFees || 0) >= 3000 ? 3000 : property.Expense_LettingFees}
                  onChange={(e: any) => handleInputChange("Expense_LettingFees", e.value)}
                  className="flex-grow"
                  max={3000}
                />
                <InputNumber
                  id="apre-num-lettingfees"
                  data-testid="apre-num-lettingfees"
                  value={property.Expense_LettingFees}
                  onValueChange={(e) => handleInputChange("Expense_LettingFees", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="cleaning" className="w-1/3">
                Cleaning ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="cleaning"
                  data-testid="apre-sldr-cleaning"
                  value={(property.Expense_CleaningFees || 0) >= 1000 ? 1000 : property.Expense_CleaningFees}
                  onChange={(e: any) => handleInputChange("Expense_CleaningFees", e.value)}
                  className="flex-grow"
                  max={1000}
                />
                <InputNumber
                  id="apre-num-cleaning"
                  data-testid="apre-num-cleaning"
                  value={property.Expense_CleaningFees}
                  onValueChange={(e) => handleInputChange("Expense_CleaningFees", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="managementFees" className="w-1/3">
                Management fees (%) *
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="managementFees"
                  data-testid="apre-sldr-managementfees"
                  value={
                    (property.Expense_AgentsRentalCommissionRate || 0) >= 10
                      ? 10
                      : property.Expense_AgentsRentalCommissionRate
                  }
                  onChange={(e: any) => handleInputChange("Expense_AgentsRentalCommissionRate", e.value)}
                  className="flex-grow"
                  max={10}
                />
                <InputNumber
                  id="apre-num-managementfees"
                  data-testid="apre-num-managementfees"
                  value={property.Expense_AgentsRentalCommissionRate}
                  onValueChange={(e) => handleInputChange("Expense_AgentsRentalCommissionRate", e.value!)}
                  className="w-1/3"
                  max={100}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                />
              </div>
            </div>
          </div>
          {/* Right Column */}
          <div className="w-1/2 flex flex-col gap-4">
            <div className="flex items-center">
              <label htmlFor="insurance" className="w-1/3">
                Insurance ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="insurance"
                  data-testid="apre-sldr-insurance"
                  value={(property.Expense_Insurance || 0) >= 1000 ? 1000 : property.Expense_Insurance}
                  onChange={(e: any) => handleInputChange("Expense_Insurance", e.value)}
                  className="flex-grow"
                  max={1000}
                />
                <InputNumber
                  id="apre-num-insurance"
                  data-testid="apre-num-insurance"
                  value={property.Expense_Insurance}
                  onValueChange={(e) => handleInputChange("Expense_Insurance", e.value!)}
                  className="w-1/3"
                  maxFractionDigits={0}
                  minFractionDigits={0}
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="repairs" className="w-1/3">
                Repairs/maintenance ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="repairs"
                  data-testid="apre-sldr-repairs"
                  value={
                    (property.Expense_RepairsMaintenance || 0) >= 1000 ? 1000 : property.Expense_RepairsMaintenance
                  }
                  onChange={(e: any) => handleInputChange("Expense_RepairsMaintenance", e.value)}
                  className="flex-grow"
                  max={1000}
                />
                <InputNumber
                  id="apre-num-repairs"
                  data-testid="apre-num-repairs"
                  value={property.Expense_RepairsMaintenance}
                  onValueChange={(e) => handleInputChange("Expense_RepairsMaintenance", e.value!)}
                  className="w-1/3"
                  maxFractionDigits={0}
                  minFractionDigits={0}
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="gardening" className="w-1/3">
                Gardening ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="gardening"
                  data-testid="apre-sldr-gardening"
                  value={(property.Expense_GardeningFees || 0) >= 3000 ? 3000 : property.Expense_GardeningFees}
                  onChange={(e: any) => handleInputChange("Expense_GardeningFees", e.value)}
                  className="flex-grow"
                  max={3000}
                />
                <InputNumber
                  id="apre-num-gardening"
                  data-testid="apre-num-gardening"
                  value={property.Expense_GardeningFees}
                  onValueChange={(e) => handleInputChange("Expense_GardeningFees", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="bodyCorporate" className="w-1/3">
                Body corporate ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="bodyCorporate"
                  data-testid="apre-sldr-bodycorporate"
                  value={
                    (property.Expense_BodyCorporateFees || 0) >= 10000 ? 10000 : property.Expense_BodyCorporateFees
                  }
                  onChange={(e: any) => handleInputChange("Expense_BodyCorporateFees", e.value)}
                  className="flex-grow"
                  max={10000}
                />
                <InputNumber
                  id="apre-num-bodycorporate"
                  data-testid="apre-num-bodycorporate"
                  value={property.Expense_BodyCorporateFees}
                  onValueChange={(e) => handleInputChange("Expense_BodyCorporateFees", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
            <div className="flex items-center">
              <label htmlFor="other" className="w-1/3">
                Other ($)
              </label>
              <div className="w-2/3 flex items-center gap-2">
                <Slider
                  id="other"
                  data-testid="apre-sldr-other"
                  value={(property.Expense_OtherExpenses || 0) >= 100000 ? 100000 : property.Expense_OtherExpenses}
                  onChange={(e: any) => handleInputChange("Expense_OtherExpenses", e.value)}
                  className="flex-grow"
                  max={100000}
                />
                <InputNumber
                  id="apre-num-other"
                  data-testid="apre-num-other"
                  value={property.Expense_OtherExpenses}
                  onValueChange={(e) => handleInputChange("Expense_OtherExpenses", e.value!)}
                  className="w-1/3"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="font-bold text-right mt-4">
          Total annual property expenses = ${totalAnnualExpenses?.toLocaleString()}
        </div>
      </div>

      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Lease</h3>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <label htmlFor="tenants">Tenants</label>
            <InputText
              id="tenants"
              data-testid="apre-txt-tenants"
              value={property.Lease_Tenants}
              onChange={(e) => handleInputChange(STR.FIELD_LEASE_TENANTS, e.target.value)}
            />
          </div>
          <div className="flex flex-row gap-4">
            <div className="w-1/3 flex flex-col">
              <label htmlFor="leaseStart">Lease start</label>
              <Calendar
                id="leaseStart"
                value={leaseStartDate}
                onChange={(e) =>
                  handleDateChange(STR.FIELD_LEASE_START_DATE, e.value as Date | null, setLeaseStartDate)
                }
                dateFormat={DATE_FORMAT.DDMMYYYY}
                showIcon
                className={`w-full ${errors.leaseStartDate ? "p-invalid" : ""}`}
              />
              {errors.leaseStartDate && <small className="p-error">{errors.leaseStartDate}</small>}
            </div>
            <div className="w-1/3 flex flex-col">
              <label htmlFor="leaseEnd">Lease end</label>
              <Calendar
                id="leaseEnd"
                value={leaseEndDate}
                onChange={(e) => handleDateChange(STR.FIELD_LEASE_END_DATE, e.value as Date | null, setLeaseEndDate)}
                dateFormat={DATE_FORMAT.DDMMYYYY}
                showIcon
                className={`w-full ${errors.leaseEndDate ? "p-invalid" : ""}`}
              />
              {errors.leaseEndDate && <small className="p-error">{errors.leaseEndDate}</small>}
            </div>
            <div className="w-1/3 flex flex-col">
              <label htmlFor="rentReview">Next rent review</label>
              <Calendar
                id="rentReview"
                value={rentReviewDate}
                onChange={(e) =>
                  handleDateChange(STR.FIELD_LEASE_REVIEW_DATE, e.value as Date | null, setRentReviewDate)
                }
                dateFormat={DATE_FORMAT.DDMMYYYY}
                showIcon
                className={`w-full ${errors.nextRentReviewDate ? "p-invalid" : ""}`}
              />
              {errors.nextRentReviewDate && <small className="p-error">{errors.nextRentReviewDate}</small>}
            </div>
          </div>
        </div>
      </div>
      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Pro-rata 1st year income and expenses</h3>
        </div>
        <div className="flex flex-row gap-4">
          <div className="w-1/2">Total annual property income = ${totalAnnualIncome.toLocaleString()}</div>
          <div className="w-1/2">Total annual property expenses = ${totalAnnualExpenses?.toLocaleString()}</div>
        </div>
        <div className="mt-4">
          <label>Would you like to pro-rata income and expenses for the initial year of purchase?</label>
          <div className="flex gap-4 mt-2">
            <div className="flex items-center">
              <RadioButton
                inputId="proRataYes"
                data-testid="apre-rdo-proratayes"
                name="proRata"
                value={1}
                onChange={(e) => {
                  handleInputChange("Year1ProRata", e.value);
                }}
                checked={property.Year1ProRata === 1}
              />
              <label htmlFor="proRataYes" className="ml-2">
                Yes
              </label>
            </div>
            <div className="flex items-center">
              <RadioButton
                inputId="proRataNo"
                data-testid="apre-rdo-proratano"
                name="proRata"
                value={0}
                onChange={(e) => {
                  handleInputChange("Year1ProRata", e.value);
                }}
                checked={property.Year1ProRata === 0}
              />
              <label htmlFor="proRataNo" className="ml-2">
                No
              </label>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4 mt-4">
          <div className="w-1/2 flex flex-col">
            <label htmlFor="proRataExpenses">Pro rata expenses from</label>
            <Calendar
              id="proRataExpenses"
              value={proRataExpenseDate}
              onChange={(e) =>
                handleDateChange(STR.FIELD_PRO_RATA_EXPENSE, e.value as Date | null, setProRataExpenseDate)
              }
              dateFormat={DATE_FORMAT.DDMMYYYY}
              showIcon
              disabled={property.Year1ProRata === 0}
              className={`w-full ${errors.proRataExpenseDate ? "p-invalid" : ""}`}
            />
            {errors.proRataExpenseDate && <small className="p-error">{errors.proRataExpenseDate}</small>}
          </div>
          <div className="w-1/2 flex flex-col">
            <label htmlFor="proRataIncome">Pro rata rent income from</label>
            <Calendar
              id="proRataIncome"
              value={proRataIncomeDate}
              onChange={(e) =>
                handleDateChange(STR.FIELD_PRO_RATA_INCOME, e.value as Date | null, setProRataIncomeDate)
              }
              disabled={property.Year1ProRata === 0}
              dateFormat={DATE_FORMAT.DDMMYYYY}
              showIcon
              className={`w-full ${errors.proRataIncomeDate ? "p-invalid" : ""}`}
            />
            {errors.proRataIncomeDate && <small className="p-error">{errors.proRataIncomeDate}</small>}
          </div>
        </div>
      </div>
      <div className="flex justify-between mt-4">
        <div>
          <Button
            id="apre-btn-back"
            data-testid="apre-btn-back"
            label="Purchase & loans"
            icon="pi pi-arrow-left"
            onClick={onBack}
            className="p-button-secondary"
          />
        </div>
        <div>
          <Button
            id="apre-btn-next"
            data-testid="apre-btn-next"
            label="Tax & market"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNextClick}
          />
        </div>
      </div>
    </div>
  );
};

export default RentAndExpenses;
